import React, { useEffect, useRef, useCallback } from 'react';
import './index.css';
import { ProctorProvider, useProctor } from './context/ProctorContext';
import { EventLogProvider, useEventLogContext } from './context/EventLogContext';
import { Header } from './components/header/Header';
import { LeftPanel } from './components/left/LeftPanel';
import { CenterPanel } from './components/center/CenterPanel';
import { RightPanel } from './components/right/RightPanel';
import { StartOverlay } from './components/overlays/StartOverlay';
import { CalibrationOverlay } from './components/overlays/CalibrationOverlay';
import { GazeDot } from './components/overlays/GazeDot';
import { useWebEyeTracker } from './hooks/useWebEyeTracker';
import { usePoseLandmarker } from './hooks/usePoseLandmarker';
import { useVoiceSensor } from './hooks/useVoiceSensor';
import { useKeyboardSensor } from './hooks/useKeyboardSensor';
import { useStylusSensor } from './hooks/useStylusSensor';
import { useCalibration } from './hooks/useCalibration';
import { useCamera } from './hooks/useCamera';
import { useFaceLandmarker } from './hooks/useFaceLandmarker';
import { useRppg } from './hooks/useRppg';
import { useObjectDetector } from './hooks/useObjectDetector';
import { useHandLandmarker } from './hooks/useHandLandmarker';
import { computeHomography, PAPER_DST, PAPER_CORNERS_DEFAULT, type Point2D } from './engine/Homography';
import { StateMachine } from './engine/StateMachine';
import { SceneDetector, SensorEvidence } from './engine/SceneDetector';
import { PauseTracker } from './engine/PauseTracker';
import { SCENES } from './config/scenes';
import { robustZ } from './engine/stats';
import { SensorValues } from './types';
import {
  attachScreenWatcher, buildEvidence, eventLevelToRisk, severityToRisk,
} from './engine/EvidenceBuilder';
import { detectPaperClip } from './engine/PaperClipDetector';
import type { BehaviorType } from './types';
import { IS_RECORDING_MODE } from './config/constants';
import { ModalitySemanticDetector, type SemanticVerdict } from './engine/ModalitySemanticDetector';
import { CoOccurrenceDetector } from './engine/CoOccurrenceDetector';
import { classifyBodyLean, gazeAreaPhrase, handPhrase, bodyPhrase, verdictRisk } from './engine/ModalityVerdict';
import type { GazeZone } from './engine/GazeZoneClassifier';

const VIDEO_ELEMENT_ID = 'webcamVideo';
// `?bodyLeanFlip=1` swaps the body left/right label at runtime (shared
// convention with the live demo).
const BODY_LEAN_FLIP: boolean =
  typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('bodyLeanFlip') === '1';

function AppContent() {
  const {
    phase, setPhase, config, gazeData, setGazeData,
    setCurrentState, pushStateHistory, addDetectedScene,
    setSensorValues, baselines, baselineStore, refreshBaselines,
    setCalibStep, calibStep, deviceIsFlat,
  } = useProctor();

  const { addEvent } = useEventLogContext();

  // ── Camera (ensures video stream) ──
  const camera = useCamera(VIDEO_ELEMENT_ID);

  // Shared refs — lets face landmarker snap gaze to detected objects
  // and map gaze through paper homography.
  const detectionsRef = useRef<import('./hooks/useObjectDetector').Detection[]>([]);
  // Tracks which object labels are currently "in view" so we only emit an
  // enter-edge event the first time each appears (avoid evidence spam at 5fps).
  // Mapping is label → firstSeenT (ms). Cleared when label exits the frame.
  // Policy: 대표님 2026-05-12 카톡 — 객체가 사용되지 않아도 존재만으로 기록.
  const lastObjectsRef = useRef<Map<string, number>>(new Map());
  // 3꼭지 PaperClip — severity transition edge에서만 evidence 발화.
  // 'none' → 'minor'/'major' 또는 'minor' → 'major' 전이 시 1회만.
  // 'major'/'minor' → 'none' 회복도 1회 기록 (감독자가 회복 시점을 안다).
  const lastPaperClipSevRef = useRef<'none' | 'minor' | 'major'>('none');
  // Initialised to the shared default paper quad (same as the live demo) so
  // the paper outline renders at a sensible desk position without requiring
  // calibration — keeps the scene-log paper display consistent with the
  // live demo. Calibration still overrides these when run.
  const paperHomographyRef = useRef<Float64Array | null>(null);
  const paperCornersRef = useRef<Point2D[]>([...PAPER_CORNERS_DEFAULT]);
  // Frozen calibration baseline — never updated per-frame (fallback if markers lost)
  const calibHomographyRef = useRef<Float64Array | null>(null);
  const calibCornersRef = useRef<Point2D[]>([...PAPER_CORNERS_DEFAULT]);

  // ── Face Detection (primary: MediaPipe FaceLandmarker) ──
  // Pose-based head estimate ref — for fallback when face is lost
  const poseHeadRef = useRef<import('./hooks/usePoseLandmarker').PoseHeadEstimate>(
    { yaw: 0, pitch: 0, noseX: 0.5, noseY: 0.3, valid: false },
  );
  // Pose-derived auxiliary ref (shoulder angle + head estimate) fed into
  // the head-pose ray so the overlay can cross-check body cues.
  const poseAuxRef = useRef<import('./hooks/useFaceLandmarker').PoseAuxInfo | null>(null);
  // Per-modality semantic pipeline (gaze-area / hand / body) + co-occurrence.
  const gazeZoneRef = useRef<GazeZone | null>(null);
  const handGripRef = useRef<{ phone: boolean; pen: boolean }>({ phone: false, pen: false });
  const gazeAreaDetRef = useRef(new ModalitySemanticDetector({ baseline: ['forward', 'own_paper'] }));
  const handDetRef = useRef(new ModalitySemanticDetector({ baseline: ['none', 'pen'] }));
  const bodyDetRef = useRef(new ModalitySemanticDetector({ baseline: ['upright'] }));
  const coOccRef = useRef(new CoOccurrenceDetector(1500, 2));
  const verdictActiveRef = useRef<Record<string, boolean>>({});

  const face = useFaceLandmarker(VIDEO_ELEMENT_ID, 'overlayCanvas', detectionsRef, paperHomographyRef, paperCornersRef, poseHeadRef, calibHomographyRef, calibCornersRef, poseAuxRef);

  // ── rPPG heart-rate (runs throughout calibration + monitoring so Step 3
  //    already has a valid bpm by the time the user reaches it) ──
  const rppg = useRppg(
    VIDEO_ELEMENT_ID,
    face.landmarksRef,
    phase === 'calibrating' || phase === 'monitoring',
  );

  // ── Object Detection (EfficientDet-Lite0, exam-relevant COCO classes) ──
  const objectDetector = useObjectDetector(VIDEO_ELEMENT_ID, phase === 'monitoring');
  detectionsRef.current = objectDetector.detections;

  // ── Hand Detection (MediaPipe HandLandmarker) ──
  // Draw to a SEPARATE canvas from the face overlay. Both hooks run
  // independent detection loops and each clearRect()s its target canvas;
  // sharing one canvas made them erase each other's drawings every frame
  // (the visible flicker). Separate canvases = no clear-war.
  const hand = useHandLandmarker(VIDEO_ELEMENT_ID, 'overlayCanvasHand', phase === 'monitoring');

  // ── Paper homography (computed during calibration, used during monitoring) ──
  const handlePaperCalibrated = useCallback((H: Float64Array, corners: Point2D[]) => {
    paperHomographyRef.current = H;
    paperCornersRef.current = corners;
    // Store frozen calibration baseline for fallback
    calibHomographyRef.current = new Float64Array(H);
    calibCornersRef.current = [...corners];
  }, []);

  // Seed a default paper homography from the shared default corners so the
  // outline shows immediately (matches the live demo). Calibration replaces
  // it via handlePaperCalibrated when run.
  useEffect(() => {
    const H = computeHomography(
      PAPER_CORNERS_DEFAULT as [Point2D, Point2D, Point2D, Point2D], PAPER_DST,
    );
    if (H) {
      paperHomographyRef.current = H;
      calibHomographyRef.current = new Float64Array(H);
    }
  }, []);

  // ── Optional: WebEyeTracker (enhances gaze with BlazeGaze model) ──
  const eyeTracker = useWebEyeTracker(VIDEO_ELEMENT_ID);

  // Use WebEyeTracker gaze if available, otherwise fall back to FaceLandmarker iris gaze
  const activeGaze = eyeTracker.gazeData ?? face.gazeData;

  // ── Body Pose (separate PoseLandmarker for shoulders) ──
  const poseData = usePoseLandmarker(VIDEO_ELEMENT_ID);
  poseHeadRef.current = poseData.head;
  poseAuxRef.current = { ...poseData.head, shoulderAngle: poseData.shoulderAngle };
  // Inputs for the per-modality semantic tick (gaze-area / hand / body).
  // face.gazeZone is the app's already-validated zone (correct direction),
  // so the gaze-area verdict reuses it directly — no sign re-derivation.
  gazeZoneRef.current = face.gazeZone;
  handGripRef.current = { phone: hand.phoneGrip.isGrip, pen: hand.penGrip.isGrip };

  // Head-pose ray sign-flip shortcuts — lets the reviewer flip X/Y live
  // while tuning. Keys X / Y toggle; state persists in localStorage.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.key === 'x' || e.key === 'X') {
        const cur = localStorage.getItem('rayFlipX') === '1';
        localStorage.setItem('rayFlipX', cur ? '0' : '1');
        console.log('[ray] flipX =', !cur);
      } else if (e.key === 'y' || e.key === 'Y') {
        const cur = localStorage.getItem('rayFlipY') === '1';
        localStorage.setItem('rayFlipY', cur ? '0' : '1');
        console.log('[ray] flipY =', !cur);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const voice = useVoiceSensor();
  const keyboard = useKeyboardSensor(baselines);
  const stylus = useStylusSensor(baselines);

  // ── Engines (in refs, mutable) ──
  const stateMachineRef = useRef(new StateMachine());
  const sceneDetectorRef = useRef(new SceneDetector());
  const pauseTrackerRef = useRef(new PauseTracker());
  const lastRuleUpdateRef = useRef(0);
  /** Monitoring 시작 performance.now 시각 — EvidenceContext.systemState.monitoringSec 계산용. */
  const monitoringStartRef = useRef<number | null>(null);

  // ── Screen visibility/focus watcher (EvidenceContext.screenContext) ──
  useEffect(() => attachScreenWatcher(), []);

  // Refs for sensor data accessed inside monitoring interval (avoids stale closures)
  const gazeRef = useRef(activeGaze);
  gazeRef.current = activeGaze;
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const keyboardRef = useRef(keyboard);
  keyboardRef.current = keyboard;
  const stylusRef = useRef(stylus);
  stylusRef.current = stylus;
  const poseRef = useRef(poseData);
  poseRef.current = poseData;

  // ── Calibration ──
  const handleCalibComplete = useCallback(() => {
    refreshBaselines();
    setPhase('monitoring');
  }, [refreshBaselines, setPhase]);

  const calibration = useCalibration(
    phase === 'calibrating',
    baselineStore,
    {
      eyeTracker: eyeTracker.isReady ? {
        startCalibration: eyeTracker.startCalibration,
        endCalibration: eyeTracker.endCalibration,
      } : null,
      voice,
      keyboard,
      stylus,
    },
    { yaw: activeGaze?.yaw ?? 0, pitch: activeGaze?.pitch ?? 0 },
    { shoulderAngle: poseData.shoulderAngle },
    handleCalibComplete,
  );

  // Sync calibStep to context
  useEffect(() => {
    setCalibStep(calibration.calibStep);
  }, [calibration.calibStep, setCalibStep]);

  // ── Sync gaze data to context ──
  useEffect(() => {
    if (activeGaze) {
      setGazeData(activeGaze);
    }
  }, [activeGaze, setGazeData]);

  // ── Main monitoring loop (~10Hz) ──
  // Uses refs to avoid stale closures and prevent interval re-creation on every sensor update
  useEffect(() => {
    if (phase !== 'monitoring') return;

    // Reset pause timers when monitoring (re)starts
    pauseTrackerRef.current.reset();
    gazeAreaDetRef.current.reset();
    handDetRef.current.reset();
    bodyDetRef.current.reset();
    coOccRef.current.reset();
    verdictActiveRef.current = {};
    monitoringStartRef.current = performance.now();

    const interval = setInterval(() => {
      const now = Date.now();
      const gaze = gazeRef.current;
      const v = voiceRef.current;
      const kb = keyboardRef.current;
      const sty = stylusRef.current;
      const pose = poseRef.current;
      if (!gaze) return;

      const hasFace = gaze.gazeState === 'open';
      const sm = stateMachineRef.current;
      const pauseTracker = pauseTrackerRef.current;

      // EvidenceContext factory closure — captures phase/mode/recording/face
      // for this tick. Used to enrich every addEvent / addDetectedScene
      // call with the 4-part evidence (timestamp + behavior + system + screen).
      const evidenceFor = (behaviorType: BehaviorType) => buildEvidence(behaviorType, {
        phase, mode: config.mode, recordingMode: IS_RECORDING_MODE,
        monitoringStartT: monitoringStartRef.current, faceVisible: hasFace,
      });

      // Feed pause tracker every tick (gaze + voice; kb/stylus already track lastInputTime)
      pauseTracker.updateGaze(gaze, now);
      pauseTracker.updateVoice(v.isSpeaking, now);

      // Get recent key interval median
      const kbIntervals = kb.getIntervals();
      const recentKb = kbIntervals.filter((k: any) => now - k.t < 5000);
      const recentKeyIntervalMs = recentKb.length >= 2
        ? recentKb.map((k: any) => k.v).sort((a: number, b: number) => a - b)[Math.floor(recentKb.length / 2)]
        : 0;

      // Determine new state
      const newState = sm.determineState({
        yaw: gaze.yaw,
        pitch: gaze.pitch,
        isSpeaking: v.isSpeaking,
        continuousSpeechSec: v.continuousSpeechSec,
        keyboardZ: kb.keyboardZ,
        recentKeyIntervalMs,
        lastInputTime: Math.max(kb.lastInputTime, sty.lastInputTime),
        isDrawing: sty.isDrawing,
        hasFace,
        deviceIsFlat,
        config,
      });

      const changed = sm.transition(newState);
      if (changed) {
        setCurrentState(newState);
        pushStateHistory({ state: newState, t: Date.now() });
      }

      // Update sensor Z-scores and check scenes (~2Hz)
      if (now - lastRuleUpdateRef.current > 500) {
        lastRuleUpdateRef.current = now;

        const gazeDeviation = Math.sqrt(gaze.yaw * gaze.yaw + gaze.pitch * gaze.pitch);
        const gazeZ = baselines.yaw
          ? robustZ(
              gazeDeviation,
              Math.sqrt(baselines.yaw.med ** 2 + (baselines.pitch?.med ?? 0) ** 2),
              Math.max(baselines.yaw.mad, baselines.pitch?.mad ?? 1) * 1.4,
            )
          : 0;

        const keyZ = kb.keyboardZ;
        const voiceSDR = v.voiceSDR;
        const styMax = Math.max(Math.abs(sty.stylusPressureZ), Math.abs(sty.stylusSpeedZ));
        const bodyZ = baselines.shoulderAngle
          ? robustZ(pose.shoulderAngle, baselines.shoulderAngle.med, baselines.shoulderAngle.mad)
          : 0;

        // Scene detection with sensor evidence + pause snapshot
        if (sm.history.length >= 2) {
          const pauses = pauseTracker.snapshot(now, kb.lastInputTime, sty.lastInputTime);
          const evidence: SensorEvidence = {
            gazeZ, keyboardZ: keyZ, voiceSDR, stylusZ: styMax, bodyZ, pauses,
          };
          const detected = sceneDetectorRef.current.checkScenes(
            sm.history, SCENES, config, evidence,
          );
          if (detected) {
            addDetectedScene({
              ...detected,
              evidence: evidenceFor('scene'),
              risk: severityToRisk(detected.scene.severity),
            });
          }
        }

        const sv: SensorValues = {
          gaze:     { value: gazeDeviation.toFixed(1) + '°', zScore: gazeZ, active: hasFace },
          keyboard: { value: kb.keyCount > 0 ? kb.keyCount + ' keys' : '--', zScore: keyZ, active: kb.keyCount > 0 },
          voice:    { value: voiceSDR.toFixed(1) + '%', zScore: voiceSDR > config.voicePct ? 2.5 : voiceSDR > 10 ? 1 : 0, active: true },
          stylus:   { value: sty.isDrawing ? styMax.toFixed(1) : '--', zScore: styMax, active: sty.isDrawing },
          body:     { value: pose.bodyVisible ? pose.shoulderAngle.toFixed(1) + '°' : '--', zScore: Math.abs(bodyZ), active: pose.bodyVisible },
          focus:    { value: '--', zScore: 0, active: false },
        };
        setSensorValues(sv);

        // ── Event logging (4 columns) ──
        // Col 0: gaze/headpose/bodypose — always log head pose
        const gazeLevel = Math.abs(gazeZ) >= config.zThresh ? 'anomaly' as const
                        : Math.abs(gazeZ) >= config.zThresh * 0.7 ? 'warning' as const
                        : 'normal' as const;
        addEvent(0, {
          t: now,
          channel: '시선',
          level: gazeLevel,
          msg: `gaze(${gaze.normPog[0].toFixed(2)},${gaze.normPog[1].toFixed(2)}) head ${gaze.yaw.toFixed(1)}°,${gaze.pitch.toFixed(1)}°`,
          zScore: gazeZ,
          evidence: evidenceFor('gaze'),
          risk: eventLevelToRisk(gazeLevel),
        });
        if (pose.bodyVisible) {
          const bodyLevel = Math.abs(bodyZ) >= config.zThresh ? 'anomaly' as const : 'normal' as const;
          addEvent(0, {
            t: now, channel: '자세',
            level: bodyLevel,
            msg: `어깨 ${pose.shoulderAngle.toFixed(1)}°`, zScore: bodyZ,
            evidence: evidenceFor('posture'),
            risk: eventLevelToRisk(bodyLevel),
          });
        }

        // Col 0 (객체): enter edge — 휴대폰/책/노트북 등 주의 대상 객체가 시야에
        // 처음 들어올 때 1회 기록. 사용 여부와 무관 (대표님 2026-05-12 카톡).
        // 워딩 중립: "관찰" — 사용 단정 ❌, 존재 사실 ✓.
        const currentObjectLabels = new Set<string>();
        for (const det of detectionsRef.current) {
          if (det.salience === 'none') continue;
          currentObjectLabels.add(det.label);
          if (!lastObjectsRef.current.has(det.label)) {
            lastObjectsRef.current.set(det.label, now);
            const objLevel: 'warning' | 'normal' = det.salience === 'high' ? 'warning' : 'normal';
            addEvent(0, {
              t: now, channel: '객체',
              level: objLevel,
              msg: `${det.labelKo} 관찰 (신뢰도 ${(det.score * 100).toFixed(0)}%)`,
              zScore: det.score,
              evidence: evidenceFor('object'),
              risk: eventLevelToRisk(objLevel),
            });
          }
        }
        // Exit edge — label이 더 이상 안 보이면 ref에서 제거하여 다음
        // 재진입 시 새 evidence가 다시 발화되게 함.
        for (const label of Array.from(lastObjectsRef.current.keys())) {
          if (!currentObjectLabels.has(label)) lastObjectsRef.current.delete(label);
        }

        // Col 0 (시험지): 3꼭지 PaperClip — severity 전이 edge에서만 발화.
        // 마커가 3개 이상 추적될 때만 (안 그러면 detectPaperClip이 unknown).
        if (paperCornersRef.current.length >= 3) {
          const clipResult = detectPaperClip(paperCornersRef.current);
          const prevSev = lastPaperClipSevRef.current;
          if (clipResult.severity !== prevSev) {
            // none → minor/major or minor → major: 잘림 발생/악화
            // minor/major → none: 회복
            const isWorsening =
              (prevSev === 'none' && clipResult.severity !== 'none') ||
              (prevSev === 'minor' && clipResult.severity === 'major');
            const isRecovering =
              prevSev !== 'none' && clipResult.severity === 'none';
            if (isWorsening || isRecovering) {
              const clipLevel: 'anomaly' | 'warning' | 'normal' =
                clipResult.severity === 'major' ? 'anomaly'
                : clipResult.severity === 'minor' ? 'warning'
                : 'normal';
              addEvent(0, {
                t: now, channel: '시험지',
                level: clipLevel,
                msg: isRecovering
                  ? '시험지 위치 회복 — 4 꼭짓점 프레임 내'
                  : clipResult.narratorText,
                zScore: 1 - clipResult.visibleFraction,
                evidence: evidenceFor('scene'),
                risk: eventLevelToRisk(clipLevel),
              });
            }
            lastPaperClipSevRef.current = clipResult.severity;
          }
        }

        // Col 1: stylus — log when drawing
        if (sty.isDrawing) {
          const styLevel = styMax >= config.zThresh ? 'anomaly' as const : 'normal' as const;
          addEvent(1, {
            t: now, channel: '스타일러스',
            level: styLevel,
            msg: `P:${sty.stylusPressureZ.toFixed(1)} S:${sty.stylusSpeedZ.toFixed(1)}`,
            zScore: styMax,
            evidence: evidenceFor('stylus'),
            risk: eventLevelToRisk(styLevel),
          });
        }

        // Col 2: keyboard — log with speed + interval + timestamp
        if (kb.keyCount > 0 && kb.lastInputTime > now - 3000) {
          const intervalStr = recentKeyIntervalMs > 0 ? `${Math.round(recentKeyIntervalMs)}ms` : '--';
          const wpm = recentKeyIntervalMs > 0 ? Math.round(60000 / recentKeyIntervalMs / 5) : 0;
          const kbLevel = Math.abs(keyZ) >= config.zThresh ? 'anomaly' as const
                        : kb.isGibberish ? 'warning' as const
                        : recentKeyIntervalMs > 0 && recentKeyIntervalMs < 120 ? 'warning' as const
                        : 'normal' as const;
          addEvent(2, {
            t: now, channel: '키보드',
            level: kbLevel,
            msg: kb.isGibberish ? `무의미 입력 | ${intervalStr}/키`
                 : `${kb.keyCount}키 | ${intervalStr}/키 | ${wpm}WPM`,
            zScore: keyZ,
            evidence: evidenceFor('keyboard'),
            risk: eventLevelToRisk(kbLevel),
          });
        }

        // Col 3: voice — always log
        const vLevel = voiceSDR >= config.voicePct ? 'anomaly' as const
                     : v.continuousSpeechSec >= 2 ? 'warning' as const
                     : v.isSpeaking ? 'normal' as const
                     : null;
        if (vLevel) {
          addEvent(3, {
            t: now, channel: '음성',
            level: vLevel,
            msg: v.continuousSpeechSec >= 2 ? `연속 ${v.continuousSpeechSec.toFixed(1)}초`
                 : `SDR ${voiceSDR.toFixed(1)}%`,
            zScore: voiceSDR,
            evidence: evidenceFor('audio'),
            risk: eventLevelToRisk(vLevel),
          });
        }

        // ── 모달리티 의미 산출물 (게이즈영역 / 핸드 / 바디) + 동시 발생 ──
        // 라이브 데모와 동일한 ModalitySemanticDetector로 각 모달리티의 최종
        // 관찰 산출물(반복/지속)을 만들고, 전이(rising-edge)에서만 로그에 기록.
        {
          const tNow = performance.now();
          const handState = handGripRef.current.phone ? 'phone'
            : handGripRef.current.pen ? 'pen' : 'none';
          const bodyState = pose.bodyVisible
            ? classifyBodyLean(pose.shoulderAngle, BODY_LEAN_FLIP) : 'upright';
          const mods: Array<{
            key: string; behavior: BehaviorType; ch: string; state: string;
            det: ModalitySemanticDetector; phrase: (v: SemanticVerdict) => string;
          }> = [
            { key: 'gaze_area', behavior: 'gaze', ch: '게이즈영역',
              state: gazeZoneRef.current ?? 'forward', det: gazeAreaDetRef.current, phrase: gazeAreaPhrase },
            { key: 'hand', behavior: 'hand', ch: '핸드',
              state: handState, det: handDetRef.current, phrase: handPhrase },
            { key: 'body', behavior: 'posture', ch: '바디',
              state: bodyState, det: bodyDetRef.current, phrase: bodyPhrase },
          ];
          const signals = mods.map(m => {
            const ve = m.det.update(m.state, tNow);
            const wasActive = verdictActiveRef.current[m.key] ?? false;
            if (ve.active && !wasActive) {
              const r = verdictRisk(ve);
              addEvent(0, {
                t: now, channel: m.ch,
                level: r === 'high' ? 'anomaly' : 'warning',
                msg: m.phrase(ve),
                zScore: ve.kind === 'repeat' ? ve.count : ve.sustainedMs / 1000,
                evidence: evidenceFor(m.behavior),
                risk: r,
              });
            }
            verdictActiveRef.current[m.key] = ve.active;
            return { modality: m.key, label: ve.active ? m.phrase(ve) : '', active: ve.active };
          });
          const co = coOccRef.current.update(signals, tNow);
          if (co.justActivated) {
            addEvent(0, {
              t: now, channel: '동시',
              level: 'fusion',
              msg: `동시 발생: ${co.labels.filter(Boolean).join(' + ')}`,
              zScore: signals.filter(s => s.active).length,
              evidence: evidenceFor('scene'),
              risk: 'high',
            });
          }
        }
      }
    }, 100); // ~10Hz

    return () => clearInterval(interval);
  }, [phase, config, baselines, deviceIsFlat, setCurrentState, pushStateHistory, addDetectedScene, setSensorValues, addEvent]);

  return (
    <>
      {phase === 'ready' && <StartOverlay />}
      {phase === 'calibrating' && (
        <CalibrationOverlay
          calibStep={calibration.calibStep}
          currentCalibPoint={calibration.currentCalibPoint}
          acceptedPoints={calibration.acceptedCalibPoints}
          totalPoints={calibration.totalCalibPoints}
          sensorReadouts={calibration.sensorReadouts}
          headStable={calibration.headStable}
          onCalibPointClick={calibration.handleCalibPointClick}
          onAdvanceStep={calibration.advanceStep}
          eyeTrackerCalibClick={eyeTracker.handleCalibClick}
          stylusHandlers={{
            onPointerDown: stylus.handlePointerDown,
            onPointerMove: stylus.handlePointerMove,
            onPointerUp: stylus.handlePointerUp,
          }}
          voiceRms={voice.rmsValue}
          keyCount={keyboard.keyCount}
          stylusSamples={stylus.getBuffer().length}
          rppg={rppg}
          onPaperCalibrated={handlePaperCalibrated}
          faceNormY={face.faceNormY}
        />
      )}
      <GazeDot
        x={gazeData ? (gazeData.normPog[0] + 0.5) * window.innerWidth : 0}
        y={gazeData ? (gazeData.normPog[1] + 0.5) * window.innerHeight : 0}
        active={phase === 'monitoring' && gazeData?.gazeState === 'open'}
      />
      <Header />
      <main className="main">
        <LeftPanel
          onKeyDown={keyboard.handleKeyDown}
          stylusHandlers={{
            onPointerDown: stylus.handlePointerDown,
            onPointerMove: stylus.handlePointerMove,
            onPointerUp: stylus.handlePointerUp,
          }}
          roll={face.roll}
          fps={face.fps}
          hasFace={face.hasFace}
          eyeTrackerStatus={eyeTracker.isReady ? 'BlazeGaze' : eyeTracker.error ? `Error: ${eyeTracker.error}` : 'Loading...'}
          gazeZone={face.gazeZone}
          detections={objectDetector.detections}
        />
        <CenterPanel />
        <RightPanel />
      </main>
    </>
  );
}

function App() {
  return (
    <ProctorProvider>
      <EventLogProvider>
        <AppContent />
      </EventLogProvider>
    </ProctorProvider>
  );
}

export default App;
